/**
 * Sentry 초기화 및 설정
 * 이 파일은 main.tsx보다 먼저 import되어야 합니다.
 */
import * as Sentry from '@sentry/react';
import type { AnyRouter } from '@tanstack/react-router';
import { config } from '@/shared/config';
import { applyApiErrorFingerprint, setWindowTag } from './sentryTags';

// Sentry가 활성화되어야 하는지 확인
const shouldInitSentry = config.sentryDsn && import.meta.env.PROD;

/**
 * Sentry 11 의 dataCollection 기본값은 요청·응답 본문·쿠키·헤더·사용자 정보를 모두 모은다
 * (sendDefaultPii 삭제). 스팬에 실리는 값은 beforeSend 를 거치지 않으므로 여기서 v10 의
 * sendDefaultPii 기본값(false) 수준으로 끈다. 백엔드 instrument.ts 의 같은 이름 상수와 짝이다.
 */
export const SENTRY_DATA_COLLECTION = {
  userInfo: false,
  cookies: false,
  httpHeaders: { deny: ['authorization', 'cookie', 'set-cookie'] },
  httpBodies: [],
  urlQueryParams: true,
  graphQL: { document: false, variables: false },
  genAI: { inputs: false, outputs: false },
} satisfies Sentry.BrowserOptions['dataCollection'];

if (shouldInitSentry) {
  Sentry.init({
    dsn: config.sentryDsn,
    environment: import.meta.env.MODE, // dev, staging, production 구분

    // 성능 모니터링 샘플링 비율 (10% 수집)
    tracesSampleRate: 0.1,

    // 에러 샘플링 비율 (100% 수집)
    sampleRate: 1.0,

    // Frontend → Backend trace 전파: API 요청에 sentry-trace / baggage 헤더 추가
    tracePropagationTargets: [config.apiUrl, /\/api\//],

    // 통합 설정
    integrations: [
      Sentry.browserTracingIntegration(),
      // TanStack Router 통합은 여기서 등록하지 않는다 — router 를 static import 하면
      // routeTree.gen.ts(= 전 페이지·피처)가 sentry 를 쓰는 모든 모듈의 의존성이 된다.
      // main.tsx 가 부팅 직후 attachRouterTracing(router) 로 주입한다 (아래 참조).
      // UI Click breadcrumb의 셀렉터에 컴포넌트 이름/소스파일을 포함시킨다.
      // vite.config.ts의 reactComponentAnnotation이 주입한 data-sentry-* 속성을 읽어,
      // 해시 클래스(`div.abc > button.xyz`) 대신 `div[Foo] > button[Bar]` 형태로 기록.
      // dom 외 옵션(console/fetch/history/xhr)은 기본값(true) 유지.
      Sentry.breadcrumbsIntegration({
        dom: {
          serializeAttribute: ['data-sentry-component', 'data-sentry-source-file'],
        },
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Session Replay 샘플링
    replaysSessionSampleRate: 0.1, // 일반 세션 10%
    replaysOnErrorSampleRate: 1.0, // 에러 발생 시 100%

    // 디버그 모드 (개발 환경에서만)
    debug: import.meta.env.DEV,

    dataCollection: SENTRY_DATA_COLLECTION,

    // 민감한 정보 제외
    beforeSend(event, hint) {
      // 개발 환경에서는 전송하지 않음
      if (import.meta.env.DEV) {
        return null;
      }
      // 요청 주소·방식 태그와 엔드포인트 지문은 여기서 얹는다 — 404 는 captureApiError 를
      // 타지 않아(sentryCapture.ts) 그쪽에 붙이면 잡히지 않는다. 근거는 applyApiErrorFingerprint.
      return applyApiErrorFingerprint(event, hint);
    },
  });
}

/**
 * TanStack Router 트레이싱을 init 이후에 붙인다.
 *
 * sentry.ts 는 ErrorBoundary·authStore·telemetry·queryClient 를 통해 사실상 전 모듈이
 * 의존하는 허브다. 여기서 `@/router` 를 static import 하면 routeTree.gen.ts 가 끌려와
 * 전 페이지·피처가 "sentry 를 쓰는 모든 파일"의 의존성이 된다 — 그 결과 vitest --changed
 * 가 PR 당 851 중 평균 500 파일(59%)을 선택했다. 이 한 줄을 주입식으로 뒤집으면 평균
 * 99 파일로 떨어진다(최근 19개 PR 정적 시뮬레이션). 가드: sentryImportBoundary.test.ts
 *
 * init 시점은 그대로 모듈 평가 시점을 유지한다 — 부팅 중 에러 캡처 공백을 만들지 않기 위해
 * init 자체는 옮기지 않고, router 가 필요한 통합만 나중에 addIntegration 으로 등록한다.
 */
export function attachRouterTracing(router: AnyRouter): void {
  if (!shouldInitSentry) return;
  Sentry.addIntegration(Sentry.tanstackRouterBrowserTracingIntegration(router));
}

/**
 * 윈도우 전환을 Sentry navigation span 으로 기록한다.
 * 이 앱은 desktop OS 스타일이라 URL이 거의 변하지 않는다 — TanStack Router 통합만으로는
 * 페이지별 성능 분리가 안 되므로, focusedWindow 변경을 SPA navigation 으로 수동 발사한다.
 */
export function trackWindowNavigation(windowId: string): void {
  const client = Sentry.getClient();
  if (!client) return;

  setWindowTag(windowId);

  // Breadcrumb trail에 window 전환 흔적을 남김 — 에러 직전 어떤 윈도우였는지 보임
  Sentry.addBreadcrumb({
    category: 'navigation',
    type: 'navigation',
    message: `window:${windowId}`,
    level: 'info',
  });

  Sentry.startBrowserTracingNavigationSpan(client, {
    name: `window:${windowId}`,
    op: 'navigation',
    attributes: {
      'sentry.source': 'custom',
      'window.id': windowId,
    },
  });
}

/**
 * 태그를 붙이는 함수들은 `sentryTags.ts` 가 소유한다 — 태그 이름이 한 파일에만 있게 하려는 것이다.
 * 불러 쓰는 쪽(`authStore` 등)이 계속 `@/shared/lib/sentry` 를 보게 여기서 그대로 다시 내보낸다.
 */
export { setBusinessContext, setScenarioContext, applyApiErrorFingerprint } from './sentryTags';
export type { BusinessContext } from './sentryTags';

export { Sentry };
